import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { FlexContainer, PureSpinner, removeStringB64 } from 'heliocor-ui';

import { convertHeicToJPEG } from '../../utils/heic';
import { SET_NOTIFICATION } from '../../store/cafe/actionTypes'
import { Container, ContentWrapper, Paragraph, StyledIcon, StyledP } from './DragDrop.styles';

function DragDrop({
    onUploadStart, content, height, fontSize, customTitle, customInfo, limitSize, fileTypes = ['JPG', 'JPEG', 'PNG'], margin, onLoad, isCore, canUpload, ...props
}) {

    const { t } = useTranslation()
    const dispatch = useDispatch()

    const statusUpload = useSelector(store => store.general.cafeStatus)
    const isLoading = statusUpload && statusUpload === 'UPLOADING'

    const setNotification = useCallback(
        (text) => dispatch({ type: SET_NOTIFICATION, notification: { text: `warning.${text}`, type: 'error' } }),
        [dispatch]
    )

    const handleFileLoad = async (name, extension, reader) => {
        try {
            if (!reader.result) {
                throw new Error("No file data available in reader.result")
            }

            let imageData = arrayBufferToBase64(reader.result)

            if (['HEIF', 'HEIC'].includes(extension)) {
                const jpeg = await convertHeicToJPEG(imageData)
                if (!jpeg) {
                    throw new Error("Failed to convert HEIC/HEIF to JPEG")
                }
                imageData = jpeg
            } 

            await onLoad(imageData, name, isCore);
        } catch (error) {
            console.error("Error in handleFileLoad:", error.message)
            setNotification(`Failed to load file: ${error.message}`)
        }
    }
    
    const arrayBufferToBase64 = buffer => {
        const uint8Array = new Uint8Array(buffer);
        let binary = ''
        const chunkSize = 8192

        for (let i = 0; i < uint8Array.length; i += chunkSize) {
            binary += String.fromCharCode(...uint8Array.slice(i, i + chunkSize))
        }

        return btoa(binary)
    }

    const onDrop = acceptedFiles => {
        if (props.notMaxFile === true || props.notMaxFile === undefined) {
            const reader = new FileReader()
            const file = acceptedFiles[0]

            reader.onload = function () {

                var files = new Blob([reader.result], { type: 'application/pdf' })

                files.text().then(x => {

                    const isEncrypted = x.includes("Encrypt") || x.substring(x.lastIndexOf("<<"), x.lastIndexOf(">>")).includes("/Encrypt")
                    
                    if (isEncrypted) setNotification('protectedPDF')
                    else {
                        const extension = file.name.split('.').pop().toUpperCase()

                        const sizeIsValid = limitSize ? file.size < limitSize : true
                        const extensionIsValid = fileTypes.includes(extension)

                        if (!extensionIsValid) return setNotification('notSupported')

                        if (!sizeIsValid) return setNotification('File size exceed the limit')

                        if (canUpload) {
                            onUploadStart()
                            const minLoadingTime = new Promise((resolve) => { setTimeout(resolve, 1000) })
                            return Promise.all([minLoadingTime, handleFileLoad(file.name, extension, reader)])
                        }
                    }
                })
            }
            reader.readAsArrayBuffer(file);
        }
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        noClick: props.clickable ? !props.clickable : true,
        disabled: isLoading || !canUpload
    })

    return (
        <Container height={height} margin={margin} {...getRootProps({ isDragActive, content })}>
            <input {...getInputProps()} />
            {content
                ? <ContentWrapper>{content}</ContentWrapper>
                : <FlexContainer alignItems='center' column>
                    {isLoading ?
                        <PureSpinner color='grey500' />
                        : <FlexContainer column alignItems='center'>
                            <StyledIcon />
                            <Paragraph color='main' fontSize={fontSize} value={t(customTitle || "button.clickAndDrop")} />
                        </FlexContainer>}
                    {customInfo && !isLoading && <StyledP warning>{t(customInfo.id || '', customInfo.data || {})}</StyledP>}
                    {fileTypes && !isLoading && <StyledP>{t('acceptedFiles')}: {fileTypes.toString().replace(/,/g, ', ')}</StyledP>}
                    {limitSize && !isLoading && <StyledP>{t('maxSize')}: {`${limitSize/1000000} MB`}</StyledP>}
                </FlexContainer>}
        </Container>
    )
}



export default DragDrop